import { useMutation } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Box, Button, Tooltip } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { CersDecisionStatus, StaffCersSubmittalsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_CERS_SUBMITTALS } from "hooks/useStaffCersSubmittals";
import { useCallback, useMemo, useState } from "react";
import { downloadFromUrl } from "util/download";

import { CersSubmittalRow, decisionStatusColor, sortModulesNotAcceptedFirst } from "./cersStatus";
import { SubmittalDetailDrawer } from "./SubmittalDetailDrawer";

const EXPORT_STAFF_CERS_SUBMITTALS = gql(`
  mutation ExportStaffCersSubmittals($search: String) {
    exportStaffCersSubmittals(search: $search) {
      success
      downloadUrl
      errorMsg
    }
  }
`);

// ── Stable, module-level config (V2 requires stable prop references) ──
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "submittedDate", sort: "desc" }];
// Only submittal-level text fields are filterable today. Module / status / date
// filtering lives on the child CersModuleDecision (needs an EXISTS subquery / range
// operator the omnisearch builder doesn't support) — deferred follow-up.
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Facility", fields: ["facilityName"] },
  { name: "Organization", fields: ["organizationName"] },
  { name: "CUPA", fields: ["localRegulatorDistrict"] },
];

// The Modules cell truncates to this many chips (plus a "+N" overflow chip) so a
// submittal with many modules can't wrap past the fixed row height into the next
// row. The full set is in the hover tooltip and the row's detail drawer.
const MAX_VISIBLE_MODULES = 3;

const getItems = (data: StaffCersSubmittalsQuery) => data.staffCersSubmittals.items ?? [];
const getCount = (data: StaffCersSubmittalsQuery) => data.staffCersSubmittals.count ?? 0;

function ExportButton({ search }: { search?: string }) {
  const alerts = useAlerts();
  // Alerts + download fire from onCompleted rather than effects keyed on the
  // mutation's data: Apollo doesn't clear `data` between calls, so a repeat
  // outcome (second success, same errorMsg) never re-triggers an effect.
  const [exportMutation, { loading }] = useMutation(EXPORT_STAFF_CERS_SUBMITTALS, {
    onCompleted: (data) => {
      const result = data.exportStaffCersSubmittals;
      if (result?.success) {
        alerts.success("Export ready");
      } else if (result?.errorMsg) {
        alerts.error(`Export failed: ${result.errorMsg}`);
      }
      if (result?.downloadUrl) {
        downloadFromUrl(result.downloadUrl);
      }
    },
    onError: (err) => alerts.error(`Export failed: ${err.message}`),
  });

  // Exports the rows matching the current omnisearch string (V2 hands it to us).
  const handleClick = useCallback(() => {
    exportMutation({ variables: { search } });
  }, [exportMutation, search]);

  return (
    <Button
      size="medium"
      loading={loading}
      startIcon={<FileDownload />}
      onClick={handleClick}
      aria-label="Export"
    >
      Export
    </Button>
  );
}

export const Submittals = () => {
  useBreadcrumb({ label: "Submittals", to: "/staff/cers/submittals" });

  const [selectedSubmittal, setSelectedSubmittal] = useState<CersSubmittalRow | null>(null);

  const columns: OmnisearchGridColDef<CersSubmittalRow>[] = useMemo(
    () => [
      { field: "facilityName", headerName: "Facility", flex: 0.7 },
      { field: "organizationName", headerName: "Organization", flex: 0.7 },
      { field: "localRegulatorDistrict", headerName: "CUPA", flex: 0.5 },
      {
        field: "submittedDate",
        headerName: "Submitted Date",
        flex: 0.4,
        valueGetter: (_, row) => {
          if (!row.submittedDate) return "";
          return new Date(row.submittedDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
        },
      },
      { field: "submittedBy", headerName: "Submitted By", flex: 0.5, sortable: false },
      {
        field: "moduleDecisions",
        headerName: "Modules",
        flex: 1.5,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.moduleDecisions?.length) return null;
          // Not-accepted first so rejections are never the ones clipped; show a
          // capped set on a single line (+N overflow) and list the full set on
          // hover. Clicking the row still opens the drawer with every module.
          const ordered = sortModulesNotAcceptedFirst(row.moduleDecisions);
          const visible = ordered.slice(0, MAX_VISIBLE_MODULES);
          const overflowCount = ordered.length - visible.length;
          const tooltip = (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {ordered.map((decision) => (
                <span key={decision.id}>
                  {prettyPrintEnumValue(decision.module)} — {prettyPrintEnumValue(decision.status)}
                </span>
              ))}
            </Box>
          );
          return (
            <Tooltip title={tooltip}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
                <Box sx={{ display: "flex", gap: 0.5, overflow: "hidden", minWidth: 0 }}>
                  {visible.map((decision) => (
                    <StatusChip
                      key={decision.id}
                      label={prettyPrintEnumValue(decision.module)}
                      color={decisionStatusColor(decision.status)}
                    />
                  ))}
                </Box>
                {overflowCount > 0 && (
                  <StatusChip label={`+${overflowCount}`} color="default" sx={{ flexShrink: 0 }} />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "modulesDecidedCount",
        headerName: "Modules Decided",
        flex: 0.4,
        sortable: false,
        // "Decided" = CERS has reached a disposition on the module. Only Submitted
        // (queued) and UnderReview (CUPA still reviewing) are pending; every other
        // status — including NotApplicable, which is a terminal decision CERS made —
        // counts as decided.
        valueGetter: (_, row) =>
          row.moduleDecisions?.filter(
            (d) =>
              d.status !== CersDecisionStatus.Submitted &&
              d.status !== CersDecisionStatus.UnderReview
          ).length ?? 0,
      },
    ],
    []
  );

  const commandButtons = useCallback(
    ({ search }: { search: string }) => [<ExportButton key="cers-export" search={search} />],
    []
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<CersSubmittalRow>) => setSelectedSubmittal(params.row),
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGridV2
        query={GET_STAFF_CERS_SUBMITTALS}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staff-cers-submittals-v2"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        filterGroups={FILTER_GROUPS}
        columns={columns}
        commandButtons={commandButtons}
        buildVariables={buildVariables}
        onRowClick={handleRowClick}
        noRowsMessage="No CERS submittals found."
      />
      <SubmittalDetailDrawer
        submittal={selectedSubmittal}
        onClose={() => setSelectedSubmittal(null)}
      />
    </Box>
  );
};
